/**
 * Resolve account insurance display status.
 * Prefer API `insurance_status` when present; for current policies also treat past
 * expire_date as expired (unless cancelled).
 *
 * @param {{ action?: string, expire_date?: string, insurance_status?: string } | null | undefined} insurance
 * @param {{ applyExpireOverride?: boolean }} [options]
 *   applyExpireOverride — default true for summary/list; set false for history rows
 *   that should keep Renewed/New labels even when the date is past.
 */

export function isInsuranceExpireDatePast(expireDate, now = new Date()) {
  if (!expireDate) return false;
  const expire = new Date(expireDate);
  if (Number.isNaN(expire.getTime())) return false;
  expire.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return expire.getTime() < today.getTime();
}

function normalizeApiInsuranceStatus(status) {
  if (status == null || status === "") return null;
  const key = String(status).trim().toLowerCase().replace(/[_-]+/g, " ");
  if (key.includes("expired")) return "expired";
  if (key.includes("cancel")) return "cancelled";
  if (key.includes("no insurance") || key === "none") return "none";
  if (key.includes("renew")) return "renewed";
  if (key.includes("new")) return "new";
  if (key.includes("active")) return "active";
  return null;
}

export function resolveInsuranceDisplayStatus(insurance, options = {}) {
  const applyExpireOverride = options.applyExpireOverride !== false;

  if (!insurance) {
    return {
      kind: "none",
      color: "#dc3545",
      translationKey: "noInsurance",
      fallbackLabel: "No Insurance",
    };
  }

  const apiKind = normalizeApiInsuranceStatus(insurance.insurance_status);
  const action = insurance.action;

  if (apiKind === "cancelled" || action === "cancel") {
    return {
      kind: "cancelled",
      color: "#dc3545",
      translationKey: "cancelledPolicy",
      fallbackLabel: "Cancelled Policy",
    };
  }

  if (apiKind === "none") {
    return {
      kind: "none",
      color: "#dc3545",
      translationKey: "noInsurance",
      fallbackLabel: "No Insurance",
    };
  }

  if (!insurance.expire_date && !apiKind) {
    return {
      kind: "none",
      color: "#dc3545",
      translationKey: "noInsurance",
      fallbackLabel: "No Insurance",
    };
  }

  // Explicit API expired always wins.
  if (apiKind === "expired") {
    return {
      kind: "expired",
      color: "#dc3545",
      translationKey: "expiredPolicy",
      fallbackLabel: "Expired Policy",
    };
  }

  // Frontend safety net for current policy display (card / accounts list).
  if (applyExpireOverride && isInsuranceExpireDatePast(insurance.expire_date)) {
    return {
      kind: "expired",
      color: "#dc3545",
      translationKey: "expiredPolicy",
      fallbackLabel: "Expired Policy",
    };
  }

  if (apiKind === "active" || action === "update") {
    return {
      kind: "active",
      color: "#28a745",
      translationKey: "activePolicy",
      fallbackLabel: "Active Policy",
    };
  }

  if (apiKind === "renewed" || action === "renew") {
    return {
      kind: "renewed",
      color: "#28a745",
      translationKey: "renewedPolicy",
      fallbackLabel: "Renewed Policy",
    };
  }

  if (apiKind === "new" || action === "new") {
    return {
      kind: "new",
      color: "#28a745",
      translationKey: "newPolicy",
      fallbackLabel: "New Policy",
    };
  }

  return {
    kind: "other",
    color: "inherit",
    translationKey: null,
    fallbackLabel: insurance.insurance_status || action || null,
  };
}
